import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { 
  PhoneFrame,
  HomeScreenMockup, 
  MissionsScreenMockup, 
  WalletScreenMockup, 
  MarketplaceScreenMockup 
} from '../mockups/PhoneMockup';
import { Target, Wallet, Clock, Gift } from 'lucide-react';

const ProductSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const modules = [
    { 
      key: 'missions', 
      icon: Target, 
      Screen: MissionsScreenMockup,
      color: 'bg-[#2563EB]'
    },
    { 
      key: 'wallet', 
      icon: Wallet, 
      Screen: WalletScreenMockup,
      color: 'bg-emerald-500'
    },
    { 
      key: 'history', 
      icon: Clock, 
      Screen: HomeScreenMockup,
      color: 'bg-violet-500'
    },
    { 
      key: 'marketplace', 
      icon: Gift, 
      Screen: MarketplaceScreenMockup,
      color: 'bg-orange-500'
    }
  ];
  
  return (
    <section 
      id="product" 
      ref={ref}
      className="section-spacing relative bg-[#F8FAFC] overflow-hidden"
      data-testid="product-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase mb-4 block">
            {t.product.subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">
            {t.product.title}
          </h2>
        </motion.div>
        
        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.map((module, index) => {
            const Icon = module.icon;
            const Screen = module.Screen;
            const moduleData = t.product.modules[module.key];
            
            return (
              <motion.div
                key={module.key}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-light rounded-2xl overflow-hidden card-hover group"
                data-testid={`product-${module.key}`}
              >
                {/* Phone Preview */}
                <div className="relative h-[300px] overflow-hidden bg-gradient-to-b from-[#0d0d1a] to-[#0a0a12]">
                  <div className="absolute inset-0 flex items-start justify-center pt-4 scale-[0.48] origin-top">
                    <PhoneFrame className="w-[220px]">
                      <Screen />
                    </PhoneFrame>
                  </div>
                  {/* Gradient Overlay */}
                  <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0F172A] to-transparent" />
                </div>
                
                {/* Content */}
                <div className="p-5 bg-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-8 h-8 rounded-lg ${module.color} flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-[#0F172A]">{moduleData.title}</h3>
                  </div>
                  <p className="text-sm text-[#475569] leading-relaxed">
                    {moduleData.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
